import { useCallback, useMemo } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useQueryClient } from 'react-query';

import { ApiError, QueryEntity } from './types';

export enum FetchMethod {
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  DELETE = 'DELETE',
  PUT = 'PUT',
}

interface UseGetFetchConfig {
  method: FetchMethod;
  data?: Record<string, any>;
  isFormData?: boolean;
}

export interface FetchConfig {
  method: FetchMethod;
  mode: RequestMode;
  cache: RequestCache;
  credentials: RequestCredentials;
  headers: Headers;
}

const fetchWithToken = (url: string, accessToken: string) =>
  fetch(url, {
    method: FetchMethod.GET,
    mode: 'cors' as RequestMode,
    cache: 'no-cache' as RequestCache,
    credentials: 'same-origin' as RequestCredentials,
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
  });

export const useFetcher = () => {
  const authInfo = useAuthInfo();
  const queryClient = useQueryClient();

  const fetcher = useCallback(
    async <T = unknown>(url: string): Promise<T> => {
      let accessToken = await authInfo.tokens.getAccessToken();

      // Undefined means PropelAuth's refresh failed (e.g. waking from sleep).
      // Retry the refresh once rather than sending a guaranteed 401.
      if (accessToken === undefined) {
        await authInfo.refreshAuthInfo();
        accessToken = await authInfo.tokens.getAccessToken();
        if (accessToken === undefined) {
          throw new ApiError(401, 'Not authenticated');
        }
      }

      let res = await fetchWithToken(url, accessToken);

      if (res.status === 401) {
        await authInfo.refreshAuthInfo();
        const refreshedToken = await authInfo.tokens.getAccessToken();
        if (refreshedToken !== undefined && refreshedToken !== accessToken) {
          res = await fetchWithToken(url, refreshedToken);
        }
      }

      if (!res.ok) {
        // The backend error filter returns { statusCode, message }, but a gateway
        // error may not be JSON at all, so fall back to the status line.
        const body = await res.json().catch(() => null);

        // If the terms-of-use gate was enabled mid-session, refetch the user so
        // TermsOfUseGate redirects to the accept-terms page.
        if (res.status === 403 && body?.errorCode === 'TERMS_OF_USE_NOT_ACCEPTED') {
          queryClient.invalidateQueries([QueryEntity.User]);
        }

        throw new ApiError(res.status, body?.message ?? `${res.status}: ${res.statusText}`);
      }

      return res.json();
    },
    [authInfo, queryClient],
  );

  return fetcher;
};

const useGetFetchConfig = () => {
  // TODO: Deprecate
  const authInfo = useAuthInfo();
  const accessToken = authInfo.accessToken;

  const fetchConfigGET: FetchConfig = useMemo(
    () => ({
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    }),
    [accessToken],
  );

  const getFetchConfig = useCallback(
    ({ method, data, isFormData }: UseGetFetchConfig) => {
      const body = isFormData ? (data as FormData) : JSON.stringify(data);

      const headers: HeadersInit = isFormData
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          };

      return {
        method,
        cache: 'no-cache' as RequestCache,
        credentials: 'same-origin' as RequestCredentials,
        headers: new Headers(headers),
        body: data ? body : undefined,
      };
    },
    [accessToken],
  );

  return {
    fetchConfigGET,
    getFetchConfig,
  };
};

export default useGetFetchConfig;
